import { buildLessonPdfHtml, lessonPdfFilename } from "./lessonPdfTemplate";

/** Surface color of the PDF page — must match #hans-lesson-pdf in lessonPdfTemplate.js. */
const PDF_SURFACE = "#09090b";

/**
 * Render a lesson summary to a PDF and hand it to the browser as a download.
 *
 * The markup is injected into a hidden clipping host rather than into the
 * visible flow: it is a full-width document that would otherwise render on
 * top of the page for the duration of the export.
 *
 * @param {Object} params
 * @param {Object} params.lesson - Lesson data object.
 * @param {string} params.studentName - Student display name.
 * @param {string} [params.teacherName] - Teacher name (shown on student exports).
 * @param {string} [params.filenameSuffix] - Name appended to the download filename.
 * @returns {Promise<void>} Rejects if rendering fails; callers surface the error.
 */
export async function exportLessonPdf({ lesson, studentName, teacherName, filenameSuffix }) {
  const html2pdf = (await import("html2pdf.js")).default;
  const html = buildLessonPdfHtml({ lesson, studentName, teacherName });

  // The container is hidden by a 0x0 clipping ancestor rather than by
  // positioning the container itself: html2canvas renders a blank page when
  // its target element is position:fixed/absolute, but is unaffected by a
  // positioned ancestor. The container therefore stays in normal flow.
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:absolute;top:0;left:0;width:0;height:0;overflow:hidden;pointer-events:none;";

  const container = document.createElement("div");
  container.innerHTML = html;
  container.style.width = "800px";
  host.appendChild(container);
  document.body.appendChild(host);

  try {
    await html2pdf()
      .set({
        margin: 0,
        filename: lessonPdfFilename(lesson.title, filenameSuffix ?? studentName),
        image: { type: "jpeg", quality: 0.98 },
        // html2canvas defaults the backdrop to white. The template is a dark
        // surface with near-white text, so without this the title and practice
        // items render white-on-white and vanish from the PDF.
        html2canvas: { scale: 2, backgroundColor: PDF_SURFACE },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(container)
      .save();
  } finally {
    // Must run even when rendering throws: the injected markup carries a
    // <style> element, so a leaked container keeps affecting the live page.
    host.remove();
  }
}
